import React, { useMemo, useState } from "react";
import { Group, Skeleton, Stack, Text } from "@mantine/core";
import { Alert, Button, ConfirmModal, EmptyState, PageHeader, SectionCard } from "@xplatform/design-system";
import {
  IconAdjustments,
  IconArrowBackUp,
  IconArrowLeft,
  IconCopy,
  IconEdit,
  IconFilePlus,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { RowAction } from "@components/AdminTable";
import type { VersionListItem } from "@models/mappingConfig";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import {
  useActiveVersion,
  useConfigSchema,
  useConfigVersions,
  useCreateDraft,
  useDeleteDraft,
  useImportDraft,
} from "@hooks/mappingConfig";
import { ApiErrorAlert, ConfigGroupTabs, ImportDialog, NewDraftDialog, SchemaForm, VersionsTable } from "@features/MappingConfig";
import { bodyOf, groupFields, mappingConfigRoutes } from "@utils/mappingConfig";
import { formatDateTime } from "@utils/monitoringFormat";

const HISTORY = "history";
const DRAFTS = "drafts";

/**
 * One plugin's config: one tab per config group of the active version (LLM,
 * retrieval, QA template, ...), then its history (rollback) and open drafts.
 */
export const MappingConfigDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { service = "", pluginId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const { canManageMappingConfig: canManage } = useAdminPermissions();

  const schema = useConfigSchema(service);
  const groups = useMemo(() => (schema.data ? groupFields(schema.data) : []), [schema.data]);
  const requested = params.get("tab");
  const tab = requested === HISTORY || requested === DRAFTS || groups.some((g) => g.group === requested)
    ? (requested as string)
    : (groups[0]?.group ?? HISTORY);
  const isGroupTab = tab !== HISTORY && tab !== DRAFTS;
  const active = useActiveVersion(service, pluginId);
  const versions = useConfigVersions(service, pluginId);
  const createDraft = useCreateDraft(service, pluginId);
  const importDraft = useImportDraft(service, pluginId);
  const deleteDraft = useDeleteDraft(service, pluginId);

  const [newDraftFrom, setNewDraftFrom] = useState<{ base: string | null } | null>(null);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState<VersionListItem | null>(null);
  const [deleteError, setDeleteError] = useState<unknown>(null);

  const drafts = useMemo(() => versions.items.filter((v) => v.status === "draft"), [versions.items]);
  const importers = schema.data?.importers ?? [];

  const review = (v: VersionListItem) => navigate(mappingConfigRoutes.review(service, pluginId, v.version_id));
  const edit = (v: VersionListItem) => navigate(mappingConfigRoutes.edit(service, pluginId, v.version_id));

  // A row click opens the review; at most three icons fit the actions column.
  const actionsFor = (v: VersionListItem): RowAction[] => [
    {
      label: t("mappingConfig.actions.edit"), icon: <IconEdit size={16} />, onClick: () => edit(v),
      hidden: !canManage || v.status !== "draft",
    },
    {
      label: t("mappingConfig.actions.rollback"), icon: <IconArrowBackUp size={16} />, onClick: () => review(v),
      hidden: !canManage || v.status !== "archived",
    },
    {
      label: t("mappingConfig.actions.copyToDraft"), icon: <IconCopy size={16} />,
      onClick: () => setNewDraftFrom({ base: v.version_id }),
      hidden: !canManage,
    },
    {
      label: t("mappingConfig.actions.delete"), icon: <IconTrash size={16} />, color: "red",
      onClick: () => {
        setDeleteError(null);
        setDeleting(v);
      },
      hidden: !canManage || v.status !== "draft",
    },
  ];

  const submitNewDraft = async (comment: string) => {
    const response = await createDraft.run({ base_version_id: newDraftFrom?.base ?? null, changes: {}, comment });
    setNewDraftFrom(null);
    navigate(mappingConfigRoutes.edit(service, pluginId, response.version.version_id));
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteDraft.run(deleting.version_id);
      setDeleting(null);
      versions.reload();
    } catch (err) {
      setDeleteError(err);
    }
  };

  const activeVersion = active.data;

  return (
    <Stack gap="md">
      <Group>
        <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(mappingConfigRoutes.list())}>
          {t("mappingConfig.backToList")}
        </Button>
      </Group>
      <PageHeader
        title={`${service} / ${pluginId}`}
        description={activeVersion
          ? t("mappingConfig.activeSummary", {
              id: activeVersion.version_id,
              by: activeVersion.activated_by ?? "-",
              at: formatDateTime(activeVersion.activated_at),
            })
          : undefined}
        icon={<IconAdjustments size={28} />}
        actions={canManage ? (
          <Group gap="sm">
            {importers.length > 0 && (
              <Button variant="light" leftSection={<IconUpload size={16} />} onClick={() => setImporting(true)}>
                {t("mappingConfig.actions.import")}
              </Button>
            )}
            <Button leftSection={<IconFilePlus size={16} />} onClick={() => setNewDraftFrom({ base: null })}>
              {t("mappingConfig.actions.newDraft")}
            </Button>
          </Group>
        ) : undefined}
      />

      {schema.error && <Alert color="red" title={t("mappingConfig.loadFailed")}>{schema.error}</Alert>}

      {activeVersion && (
        <SectionCard>
          <Group gap="lg">
            <Text size="sm"><b>{t("mappingConfig.columns.version")}:</b> <Text span ff="monospace">{activeVersion.version_id}</Text></Text>
            <Text size="sm"><b>{t("mappingConfig.columns.createdBy")}:</b> {activeVersion.created_by}</Text>
            {activeVersion.comment && <Text size="sm" c="dimmed" lineClamp={2}>{activeVersion.comment}</Text>}
          </Group>
        </SectionCard>
      )}

      <ConfigGroupTabs
        groups={groups}
        value={tab}
        onChange={(next) => setParams({ tab: next }, { replace: true })}
        extraTabs={[
          { value: HISTORY, label: t("mappingConfig.tabs.history") },
          { value: DRAFTS, label: t("mappingConfig.tabs.drafts", { count: drafts.length }) },
        ]}
      />

      {isGroupTab && (
        <>
          {active.error && <Alert color="red" title={t("mappingConfig.loadFailed")}>{active.error}</Alert>}
          {(active.loading && !active.data) || (schema.loading && !schema.data) ? (
            <Skeleton h={240} radius="md" />
          ) : activeVersion && schema.data ? (
            <SchemaForm
              schema={schema.data}
              value={bodyOf(activeVersion, schema.data.json_schema)}
              readOnly
              activeGroup={tab}
            />
          ) : !active.error ? (
            <EmptyState title={t("mappingConfig.noActive")} description={t("mappingConfig.noActiveHint")} />
          ) : null}
        </>
      )}

      {tab === HISTORY && (
        <>
          {versions.error && <Alert color="red" title={t("mappingConfig.loadFailed")}>{versions.error}</Alert>}
          <SectionCard>
            <VersionsTable
              items={versions.items}
              loading={versions.loading}
              hasMore={versions.hasMore}
              onLoadMore={versions.loadMore}
              onRowClick={review}
              actions={actionsFor}
            />
          </SectionCard>
        </>
      )}

      {tab === DRAFTS && (
        <SectionCard>
          <VersionsTable
            items={drafts}
            loading={versions.loading}
            hasMore={versions.hasMore}
            onLoadMore={versions.loadMore}
            onRowClick={review}
            actions={actionsFor}
            emptyMessage={t("mappingConfig.noDrafts")}
          />
        </SectionCard>
      )}

      <NewDraftDialog
        opened={newDraftFrom !== null}
        baseVersionId={newDraftFrom?.base ?? null}
        onClose={() => setNewDraftFrom(null)}
        onSubmit={submitNewDraft}
      />

      <ImportDialog
        opened={importing}
        importers={importers}
        onClose={() => setImporting(false)}
        onSubmit={async (body) => {
          const response = await importDraft.run(body);
          setImporting(false);
          navigate(mappingConfigRoutes.review(service, pluginId, response.version.version_id), {
            state: { importReport: response.import_report },
          });
        }}
      />

      <ConfirmModal
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t("mappingConfig.deleteDraft.title")}
        confirmLabel={t("mappingConfig.actions.delete")}
        cancelLabel={t("common.cancel")}
        loading={deleteDraft.loading}
        portalProps={{ target: "#admin-portal-root" }}
        danger
      >
        <Stack gap="sm">
          <Text size="sm">{t("mappingConfig.deleteDraft.body", { id: deleting?.version_id ?? "" })}</Text>
          {deleteError !== null && <ApiErrorAlert error={deleteError} />}
        </Stack>
      </ConfirmModal>
    </Stack>
  );
};
